import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import UpdateUsdtBalanceModal from '../components/UpdateUsdtBalanceModal';

const CURRENCIES = ['USDT', 'BTC', 'ETH', 'USDC'];

const formatBalance = (value, currency, fallback = '0.00') => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const decimals = ['BTC', 'ETH'].includes(currency) ? 8 : 2;
  return parsed.toFixed(decimals);
};

const AdminUserWalletBalance = () => {
  const [rows, setRows] = useState([]);
  const [searchUid, setSearchUid] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedRow, setSelectedRow] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');

  const lastFetchId = useRef(0);

  useEffect(() => {
    // Set items per page based on screen size
    const updateItemsPerPage = () => {
      setItemsPerPage(window.innerWidth < 768 ? 5 : 10);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const fetchAll = async (currency) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/admin/balances/${currency}`,
      );
      if (response.data?.success && response.data?.data?.users) {
        setRows(response.data.data.users);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      toast.error(`Failed to fetch ${currency} balances`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchByUid = async (uid, currency) => {
    const fetchId = ++lastFetchId.current;

    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/admin/users/${uid}/balance/${currency}`,
      );

      // Ignore stale responses
      if (fetchId !== lastFetchId.current) return;

      if (response.data?.success && response.data?.data) {
        // API returns a single object in data
        setRows([response.data.data]);
      } else {
        setRows([]);
      }
    } catch (error) {
      if (fetchId !== lastFetchId.current) return;

      console.error('Error fetching user balance:', error);
      const msg = error.response?.data?.message || 'No user found for this UID';
      toast.error(msg);
      setRows([]);
    } finally {
      if (fetchId === lastFetchId.current) setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchAll(selectedCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when currency tab changes
  useEffect(() => {
    const uid = searchUid.trim();
    if (uid && /^\d+$/.test(uid)) {
      fetchByUid(uid, selectedCurrency);
    } else {
      fetchAll(selectedCurrency);
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  // Search behavior: when UID is provided, call the UID endpoint; clearing search restores all.
  useEffect(() => {
    const uid = searchUid.trim();

    const timer = setTimeout(() => {
      if (!uid) {
        fetchAll(selectedCurrency);
        return;
      }

      // Only allow numeric UID searches (matches existing UI usage)
      if (!/^\d+$/.test(uid)) {
        toast.error('UID must be numeric');
        setRows([]);
        setLoading(false);
        return;
      }

      fetchByUid(uid, selectedCurrency);
    }, 450);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUid]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const pagedRows = useMemo(() => {
    return rows.slice(indexOfFirstItem, indexOfLastItem);
  }, [rows, indexOfFirstItem, indexOfLastItem]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;

  const getPaginationNumbers = () => {
    const pages = [];

    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    if (currentPage >= totalPages - 2) {
      for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(currentPage);
    pages.push(currentPage + 1);
    pages.push('...');
    pages.push(totalPages - 1);
    pages.push(totalPages);
    return pages;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchUid]);

  const openUpdateModal = (row) => {
    setSelectedRow(row);
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedRow(null);
  };

  const handleUpdated = (updateData) => {
    // Try to patch the visible table row with the latest availableBalance
    const updatedUserUid =
      updateData?.user?.uid || updateData?.user?.uid === 0
        ? String(updateData.user.uid)
        : null;
    const walletUpdate = updateData?.wallet;

    if (!updatedUserUid || !walletUpdate) {
      // Fallback: refresh current view
      const uid = searchUid.trim();
      if (uid) fetchByUid(uid, selectedCurrency);
      else fetchAll(selectedCurrency);
      return;
    }

    setRows((prev) =>
      prev.map((r) => {
        if (String(r.uid) !== String(updatedUserUid)) return r;
        return {
          ...r,
          wallet: {
            ...r.wallet,
            currencyCode: r.wallet?.currencyCode || 'USDT',
            availableBalance:
              typeof walletUpdate.availableBalance !== 'undefined'
                ? walletUpdate.availableBalance
                : r.wallet?.availableBalance,
            updatedAt: new Date().toISOString(),
          },
        };
      }),
    );
  };

  return (
    <div className='container-fluid py-2 lg:py-5'>
      <div className='card shadow-sm border-0'>
        <div className='card-header bg-white border-bottom py-3'>
          <h5 className='mb-0 fw-semibold'>User Wallet Balance</h5>
        </div>

        <div className='card-body'>
          {/* Currency Selector */}
          <div className='mb-3 d-flex flex-wrap gap-2'>
            {CURRENCIES.map((cur) => (
              <button
                key={cur}
                className={`btn btn-sm rounded-pill px-3 ${
                  selectedCurrency === cur
                    ? 'btn-primary'
                    : 'btn-outline-secondary'
                }`}
                onClick={() => setSelectedCurrency(cur)}
              >
                {cur}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className='mb-3'>
            <input
              className='form-control'
              placeholder='Search by UID'
              value={searchUid}
              onChange={(e) => setSearchUid(e.target.value)}
            />
          </div>

          {loading ? (
            <div className='text-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className='table-responsive'>
                <table className='table table-hover align-middle mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th className='text-start ps-3 py-3'>UID</th>
                      <th className='text-start py-3'>Email</th>
                      <th className='text-start py-3'>Currency</th>
                      <th className='text-start py-3'>Available Balance</th>
                      <th className='text-start py-3'>Updated</th>
                      <th className='text-start py-3'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length > 0 ? (
                      pagedRows.map((u) => (
                        <tr
                          key={`${u.userId || ''}-${u.uid || ''}`}
                          style={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                          <td className='text-start ps-3 py-3'>{u.uid}</td>
                          <td className='text-start py-3'>
                            {u.email || 'N/A'}
                          </td>
                          <td className='text-start py-3'>
                            <span className='badge bg-secondary'>
                              {u.wallet?.currencyCode || 'USDT'}
                            </span>
                          </td>
                          <td className='text-start py-3'>
                            {formatBalance(
                              u.wallet?.availableBalance,
                              u.wallet?.currencyCode || selectedCurrency,
                            )}
                          </td>
                          <td className='text-start py-3'>
                            {u.wallet?.updatedAt
                              ? new Date(u.wallet.updatedAt).toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className='text-start py-3'>
                            <button
                              className='btn btn-sm btn-primary rounded-pill px-3'
                              onClick={() => openUpdateModal(u)}
                            >
                              Update
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className='text-center py-4 text-muted'>
                          No results found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {rows.length > itemsPerPage && (
                <div className='card-footer bg-white border-top py-3 mt-3'>
                  <div className='d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2'>
                    <div className='text-muted small text-center text-sm-start'>
                      Showing {indexOfFirstItem + 1} to{' '}
                      {Math.min(indexOfLastItem, rows.length)} of {rows.length}{' '}
                      users
                    </div>
                    <nav>
                      <ul className='pagination pagination-sm mb-0'>
                        <li
                          className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                        >
                          <button
                            className='page-link'
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Prev
                          </button>
                        </li>
                        {getPaginationNumbers().map((page, idx) => {
                          if (page === '...') {
                            return (
                              <li
                                key={`ellipsis-${idx}`}
                                className='page-item disabled'
                              >
                                <span className='page-link'>...</span>
                              </li>
                            );
                          }
                          return (
                            <li
                              key={page}
                              className={`page-item ${currentPage === page ? 'active' : ''}`}
                            >
                              <button
                                className='page-link'
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        })}
                        <li
                          className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                        >
                          <button
                            className='page-link'
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <UpdateUsdtBalanceModal
        isOpen={showUpdateModal}
        onClose={closeUpdateModal}
        user={selectedRow}
        onUpdated={handleUpdated}
      />
    </div>
  );
};

export default AdminUserWalletBalance;
